import { useCallback } from 'react';
import { notify } from '../components/common/AppToast';

/**
 * Actions barre d’outils dossiers (ouvrir / nouveau / dupliquer / supprimer / renommer)
 * partagées par les écrans de chiffrage (ITI, Klub, Plan de com).
 *
 * Les effets de session locaux (setData, applyDossier, warnings…) passent par onSessionApplied.
 */
export default function useChiffrageDossierActions({
  session,
  ask,
  saveTimerRef,
  flushBeforeSwitch = null,
  onSessionApplied,
  labels = {},
  /** Si true (défaut), ignore open quand id === dossierId (ITI / PDC). Klub : false. */
  skipSameDossier = true,
}) {
  const {
    dossierId,
    saving,
    openDossier,
    startNew,
    duplicateDossier,
    deleteDossier,
    renameDossier,
  } = session;

  const entity = labels.entity ?? 'chiffrage';
  const Entity = entity.charAt(0).toUpperCase() + entity.slice(1);
  const deleteTitle = labels.deleteTitle ?? `Supprimer ce ${entity} ?`;
  const deleteMessage = labels.deleteMessage ?? 'Cette action est définitive.';

  const clearSaveTimer = useCallback(() => {
    if (saveTimerRef?.current) {
      clearTimeout(saveTimerRef.current);
    }
  }, [saveTimerRef]);

  const prepareSwitch = useCallback(async () => {
    if (typeof flushBeforeSwitch === 'function') {
      await Promise.resolve(flushBeforeSwitch()).catch(() => null);
      return;
    }
    clearSaveTimer();
  }, [flushBeforeSwitch, clearSaveTimer]);

  const applySession = useCallback((full, ctx) => {
    if (!full) return;
    onSessionApplied?.(full, ctx);
  }, [onSessionApplied]);

  const handleOpenDossier = useCallback(async (id) => {
    if (!id || saving) return;
    if (skipSameDossier && id === dossierId) return;
    await prepareSwitch();
    try {
      const full = await openDossier(id);
      applySession(full, { action: 'open', id });
    } catch {
      notify.error(`Impossible d’ouvrir ce ${entity}.`);
    }
  }, [dossierId, saving, skipSameDossier, prepareSwitch, openDossier, applySession, entity]);

  const handleNewDossier = useCallback(async () => {
    if (saving) return;
    await prepareSwitch();
    try {
      const full = await startNew();
      if (full) {
        applySession(full, { action: 'new' });
        if (full.dossier?.id === dossierId) {
          notify.info(`Ce ${entity} est déjà vide, vous pouvez continuer.`);
        } else {
          notify.success(`Nouveau ${entity} prêt. L’ancien reste dans la liste.`);
        }
      }
    } catch {
      notify.error(`Impossible de créer un nouveau ${entity}.`);
    }
  }, [saving, prepareSwitch, startNew, applySession, dossierId, entity]);

  const handleDuplicateDossier = useCallback(async (id) => {
    if (!id || saving) return;
    await prepareSwitch();
    try {
      const full = await duplicateDossier(id);
      if (full) {
        applySession(full, { action: 'duplicate', id });
        notify.success(`${Entity} dupliqué. Vous travaillez sur la copie.`);
      }
    } catch {
      notify.error(`Impossible de dupliquer ce ${entity}.`);
    }
  }, [saving, prepareSwitch, duplicateDossier, applySession, Entity, entity]);

  const handleDeleteDossier = useCallback(async (id) => {
    if (!id || saving) return;
    const ok = await ask({
      title: deleteTitle,
      message: deleteMessage,
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    clearSaveTimer();
    try {
      const full = await deleteDossier(id);
      if (full) {
        applySession(full, { action: 'delete', id });
        notify.success(`${Entity} supprimé.`);
      }
    } catch {
      notify.error(`Impossible de supprimer ce ${entity}.`);
    }
  }, [
    saving,
    ask,
    deleteTitle,
    deleteMessage,
    clearSaveTimer,
    deleteDossier,
    applySession,
    Entity,
    entity,
  ]);

  const handleRenameDossier = useCallback(async (id, nextTitre) => {
    if (!id || saving) return;
    clearSaveTimer();
    try {
      const full = await renameDossier(id, nextTitre);
      if (full && id === dossierId) {
        applySession(full, { action: 'rename', id, titre: nextTitre });
      }
    } catch {
      notify.error(`Impossible de renommer ce ${entity}.`);
    }
  }, [saving, clearSaveTimer, renameDossier, dossierId, applySession, entity]);

  return {
    handleOpenDossier,
    handleNewDossier,
    handleDuplicateDossier,
    handleDeleteDossier,
    handleRenameDossier,
  };
}
